import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ExternalLink, GripVertical, Plus, Link2, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { validateLinkUrl } from "@/lib/link-validation";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskLink } from "./task-types";

function getLinkDisplay(link: TaskLink): string {
  if (link.label) return link.label;
  try {
    const parsed = new URL(link.url);
    const path = parsed.pathname === "/" ? "" : parsed.pathname;
    const display = parsed.hostname + path;
    return display.length > 60 ? display.slice(0, 57) + "..." : display;
  } catch {
    return link.url.length > 60 ? link.url.slice(0, 57) + "..." : link.url;
  }
}

function LinkItemContent({
  link,
  canEdit,
  onRemove,
  dragHandle,
  isDragOverlay,
}: {
  link: TaskLink;
  canEdit: boolean;
  onRemove: (id: string) => void;
  dragHandle?: React.ReactNode;
  isDragOverlay?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-1 py-1 ${isDragOverlay ? "rounded-md border border-border bg-background shadow-md px-1" : ""}`}
      data-testid={`task-link-${link.id}`}
    >
      <div className="flex items-center gap-1 min-w-0">
        {dragHandle}
        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm hover:underline truncate text-foreground"
          title={link.url}
          data-testid={`task-link-anchor-${link.id}`}
        >
          {getLinkDisplay(link)}
        </a>
      </div>
      {canEdit && (
        <Button variant="ghost" size="icon" onClick={() => onRemove(link.id)} data-testid={`task-button-remove-link-${link.id}`}>
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function SortableLinkItem({
  link,
  activeId,
  onRemove,
}: {
  link: TaskLink;
  activeId: string | null;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style} className="rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/30 py-3 my-0.5" />
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <LinkItemContent
        link={link}
        canEdit={true}
        onRemove={onRemove}
        dragHandle={
          <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground shrink-0">
            <GripVertical className="w-3 h-3" />
          </span>
        }
      />
    </div>
  );
}

interface TaskLinkSectionProps {
  taskId: string;
  links: TaskLink[];
  canEdit: boolean;
}

export function TaskLinkSection({ taskId, links, canEdit }: TaskLinkSectionProps) {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const addLinkMutation = useMutation({
    mutationFn: async (data: { url: string; label?: string }) => apiRequest("POST", `/api/tasks/${taskId}/links`, data),
    onSuccess: () => {
      invalidateTaskQueries();
      setNewUrl("");
      setNewLabel("");
      setUrlError(null);
      setIsAdding(false);
    },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const removeLinkMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/tasks/${taskId}/links/${linkId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => apiRequest("POST", `/api/tasks/${taskId}/links/reorder`, { orderedIds }),
    onError: (error: Error) => {
      invalidateTaskQueries();
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleUrlBlur() {
    if (!newUrl.trim()) {
      setUrlError(null);
      return;
    }
    const result = validateLinkUrl(newUrl);
    setUrlError(result.valid ? null : result.error ?? "Invalid URL");
  }

  function handleSubmit() {
    const result = validateLinkUrl(newUrl);
    if (!result.valid) {
      setUrlError(result.error ?? "Invalid URL");
      return;
    }
    setUrlError(null);
    addLinkMutation.mutate({
      url: newUrl.trim(),
      label: newLabel.trim() || undefined,
    });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = links.findIndex(l => l.id === active.id);
    const newIndex = links.findIndex(l => l.id === over.id);
    const reordered = arrayMove(links, oldIndex, newIndex);

    const cached = queryClient.getQueryData<{ task: any }>(["/api/tasks", taskId]);
    if (cached) {
      queryClient.setQueryData(["/api/tasks", taskId], {
        ...cached,
        task: { ...cached.task, links: reordered },
      });
    }

    reorderMutation.mutate(reordered.map(l => l.id));
  }

  const activeLink = activeId ? links.find(l => l.id === activeId) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-1">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
          <Link2 className="w-3 h-3" /> Links
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-links-count">
            {links.length}
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 text-muted-foreground/60 cursor-help" data-testid="task-link-info-tooltip" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">For security reasons, URL shorteners (bit.ly, tinyurl, t.co, etc.) and file-sharing services (Dropbox, WeTransfer, Mega, etc.) are not accepted. Please use full, direct URLs. Google Drive links are fine.</p>
            </TooltipContent>
          </Tooltip>
        </Label>
        {canEdit && !isAdding && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsAdding(true)}
            data-testid="task-button-add-link"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {isAdding && canEdit && (
        <div className="space-y-1.5" data-testid="task-link-add-form">
          <Input
            placeholder="https://..."
            value={newUrl}
            onChange={e => { setNewUrl(e.target.value); if (urlError) setUrlError(null); }}
            onBlur={handleUrlBlur}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm"
            maxLength={2000}
            autoFocus
            data-testid="task-input-link-url"
          />
          {urlError && (
            <p className="text-xs text-destructive" data-testid="task-text-link-error">{urlError}</p>
          )}
          <Input
            placeholder="Label (optional)"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm"
            maxLength={500}
            data-testid="task-input-link-label"
          />
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!newUrl.trim() || !!urlError || addLinkMutation.isPending}
              data-testid="task-button-submit-link"
            >
              Add
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setIsAdding(false); setNewUrl(""); setNewLabel(""); setUrlError(null); }}
              data-testid="task-button-cancel-link"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {links.length === 0 && !isAdding && (
        <p className="text-xs text-muted-foreground italic" data-testid="task-text-no-links">No links added</p>
      )}

      {canEdit && links.length > 1 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={links.map(l => l.id)} strategy={verticalListSortingStrategy}>
            {links.map(l => (
              <SortableLinkItem
                key={l.id}
                link={l}
                activeId={activeId}
                onRemove={(id) => removeLinkMutation.mutate(id)}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeLink && (
              <LinkItemContent
                link={activeLink}
                canEdit={false}
                onRemove={() => {}}
                isDragOverlay
                dragHandle={
                  <span className="cursor-grabbing text-muted-foreground shrink-0">
                    <GripVertical className="w-3 h-3" />
                  </span>
                }
              />
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        links.map(l => (
          <LinkItemContent
            key={l.id}
            link={l}
            canEdit={canEdit}
            onRemove={(id) => removeLinkMutation.mutate(id)}
          />
        ))
      )}
    </div>
  );
}
